import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';
import type { Props as LoginModalProps } from './LoginModal';
import type { PartialConfigurableText } from './types';

type ForkLoader = () => Promise<{ LoginModal: ComponentType<LoginModalProps> }>;

export type ModalVariantEntry = {
  configuredText?: PartialConfigurableText;
  forkComponent?: LazyExoticComponent<ComponentType<LoginModalProps>>;
  forkLoader?: ForkLoader;
};

// Each fork is its own dynamic-import chunk. LoginModalManager calls
// forkLoader() on mount once the real-test bucket is known, so the
// chunk is loaded well before any CTA click. React.lazy resolves
// from the browser's module cache when render-time arrives — the
// Suspense fallback (null) only kicks in if the chunk somehow
// hasn't arrived yet (very slow network + fast click), in which
// case the modal opens a beat later rather than flashing control
// before the variant.
const b4PaperThumbnailAboveLoader: ForkLoader = () =>
  import('./autotest/b4_paper_thumbnail_above/LoginModal');
const f1EmailButtonOnTopLoader: ForkLoader = () =>
  import('./autotest/f1_email_button_on_top/LoginModal');
const f2NoFacebookLoader: ForkLoader = () =>
  import('./autotest/f2_no_facebook/LoginModal');
const f4GoogleOnlyLoader: ForkLoader = () =>
  import('./autotest/f4_google_only/LoginModal');
const f5GoogleOnlyWithPaperLineLoader: ForkLoader = () =>
  import('./autotest/f5_google_only_with_paper_line/LoginModal');
const h1ComboPaperSpeedGoogleDownloadLoader: ForkLoader = () =>
  import('./autotest/h1_combo_paper_speed_google_download/LoginModal');
const h3GoogleOnlyWithSpeedLoader: ForkLoader = () =>
  import('./autotest/h3_google_only_with_speed/LoginModal');
const h4YourPaperCardGoogleOnlyLoader: ForkLoader = () =>
  import('./autotest/h4_your_paper_card_google_only/LoginModal');
const h5YourPaperCardThreeMethodsLoader: ForkLoader = () =>
  import('./autotest/h5_your_paper_card_three_methods/LoginModal');

const B4PaperThumbnailAbove = lazy(() =>
  b4PaperThumbnailAboveLoader().then((m) => ({ default: m.LoginModal })),
);
const F1EmailButtonOnTop = lazy(() =>
  f1EmailButtonOnTopLoader().then((m) => ({ default: m.LoginModal })),
);
const F2NoFacebook = lazy(() =>
  f2NoFacebookLoader().then((m) => ({ default: m.LoginModal })),
);
const F4GoogleOnly = lazy(() =>
  f4GoogleOnlyLoader().then((m) => ({ default: m.LoginModal })),
);
const F5GoogleOnlyWithPaperLine = lazy(() =>
  f5GoogleOnlyWithPaperLineLoader().then((m) => ({ default: m.LoginModal })),
);
const H1ComboPaperSpeedGoogleDownload = lazy(() =>
  h1ComboPaperSpeedGoogleDownloadLoader().then((m) => ({
    default: m.LoginModal,
  })),
);
const H3GoogleOnlyWithSpeed = lazy(() =>
  h3GoogleOnlyWithSpeedLoader().then((m) => ({ default: m.LoginModal })),
);
const H4YourPaperCardGoogleOnly = lazy(() =>
  h4YourPaperCardGoogleOnlyLoader().then((m) => ({ default: m.LoginModal })),
);
const H5YourPaperCardThreeMethods = lazy(() =>
  h5YourPaperCardThreeMethodsLoader().then((m) => ({ default: m.LoginModal })),
);

export const MODAL_VARIANT_REGISTRY: Record<string, ModalVariantEntry> = {
  control: {},

  // === v3 active arms (loswp_modal_autotest_2026_q2_v3) ===
  //
  // Naming convention: <cluster><sequence>_<description>. Cluster letter
  // is preserved across slate versions so b1/h4/etc. are comparable to
  // prior v1 / v2 results; descriptive suffix makes registry/YAML/Redshift
  // bucket data legible without a lookup table.

  b1_paper_in_header: {
    configuredText: {
      buttonsPage: {
        header: "Read '{paperTitle}' for free",
      },
    },
  },
  b2_paper_with_author_byline: {
    configuredText: {
      buttonsPage: {
        header: "Read '{paperTitle}' for free",
        subheader: 'by {authorName}',
      },
    },
  },
  b3_library_value_prop: {
    configuredText: {
      buttonsPage: {
        header: "Free academic library — including '{paperTitle}'",
        subheader: 'Get instant access',
      },
    },
  },
  b4_paper_thumbnail_above: {
    forkComponent: B4PaperThumbnailAbove,
    forkLoader: b4PaperThumbnailAboveLoader,
  },
  b5_paper_on_email_reg: {
    configuredText: {
      emailRegistrationPage: {
        subheader: "You're 30 seconds from reading '{paperTitle}'",
      },
    },
  },
  b6_paper_across_funnel: {
    configuredText: {
      buttonsPage: {
        header: "Read '{paperTitle}' for free",
      },
      emailEntryPage: {
        subheader: "To read '{paperTitle}'",
      },
      emailRegistrationPage: {
        subheader: "You're 30 seconds from reading '{paperTitle}'",
      },
    },
  },
  c2_speed_subheader: {
    configuredText: {
      buttonsPage: {
        subheader: '5 seconds with Google — really',
      },
    },
  },
  c3_almost_there_email_reg: {
    configuredText: {
      emailRegistrationPage: {
        subheader: 'Last step — almost there',
      },
    },
  },
  d1_download_oauth_labels: {
    configuredText: {
      buttonsPage: {
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        emailButton: 'Download with Email',
        disclaimerVerb: 'Download',
      },
    },
  },
  d3_download_submit_label: {
    configuredText: {
      emailRegistrationPage: {
        submit: 'Download',
      },
    },
  },
  f1_email_button_on_top: {
    forkComponent: F1EmailButtonOnTop,
    forkLoader: f1EmailButtonOnTopLoader,
  },
  f2_no_facebook: {
    forkComponent: F2NoFacebook,
    forkLoader: f2NoFacebookLoader,
  },
  f4_google_only: {
    forkComponent: F4GoogleOnly,
    forkLoader: f4GoogleOnlyLoader,
  },
  f5_google_only_with_paper_line: {
    forkComponent: F5GoogleOnlyWithPaperLine,
    forkLoader: f5GoogleOnlyWithPaperLineLoader,
    configuredText: {
      buttonsPage: { paperContextText: "To read '{paperTitle}'" },
    },
  },
  h1_combo_paper_speed_google_download: {
    forkComponent: H1ComboPaperSpeedGoogleDownload,
    forkLoader: h1ComboPaperSpeedGoogleDownloadLoader,
    configuredText: {
      buttonsPage: {
        header: "Read '{paperTitle}' for free",
        subheader: '5 seconds with Google — really',
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        facebookButton: 'Download with Facebook',
        emailButton: 'Download with Email',
        disclaimerVerb: 'Download',
      },
    },
  },
  h2_paper_plus_speed: {
    configuredText: {
      buttonsPage: {
        header: "Read '{paperTitle}' for free",
        subheader: '5 seconds with Google — really',
      },
    },
  },
  h3_google_only_with_speed: {
    forkComponent: H3GoogleOnlyWithSpeed,
    forkLoader: h3GoogleOnlyWithSpeedLoader,
    configuredText: {
      buttonsPage: {
        subheader: '5 seconds with Google — really',
      },
    },
  },
  h4_your_paper_card_google_only: {
    forkComponent: H4YourPaperCardGoogleOnly,
    forkLoader: h4YourPaperCardGoogleOnlyLoader,
    configuredText: {
      buttonsPage: {
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        emailButton: 'Download with Email',
        disclaimerVerb: 'Download',
      },
    },
  },
  h5_your_paper_card_three_methods: {
    forkComponent: H5YourPaperCardThreeMethods,
    forkLoader: h5YourPaperCardThreeMethodsLoader,
    configuredText: {
      buttonsPage: {
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        emailButton: 'Download with Email',
        disclaimerVerb: 'Download',
      },
    },
  },
  h6_your_paper_card_continue_buttons: {
    // Reuses h5's three-button card layout but omits the configuredText
    // override, so buttons render with the base default labels
    // ("Continue with Google" / "Continue with Apple" / "Continue with Email").
    // Lets us isolate the Your-paper-card layout effect from the
    // "Download with X" label change that h5 bundles in.
    forkComponent: H5YourPaperCardThreeMethods,
    forkLoader: h5YourPaperCardThreeMethodsLoader,
  },

  // === Legacy entries retained for finished v1 / v2 tests ===
  //
  // These keys appear in `config/finished_ab_tests/loswp_modal_autotest_2026_q2.yml`
  // and `loswp_modal_autotest_2026_q2_v2.yml`. They're orphaned from v3's
  // active arm list, but kept here so `git log --follow` on the finished
  // tests still resolves cleanly. The dropped variants (c1 + d2) were the
  // worst-of-20 in v2 day-1 (both contained literal "sign up" copy that
  // depressed conversion). Don't reintroduce them under v3 names.

  variant_b1: {
    configuredText: {
      buttonsPage: {
        header: "To read '{paperTitle}' please sign up",
      },
    },
  },
  variant_b2: {
    configuredText: {
      buttonsPage: {
        header: "Sign up to read '{paperTitle}'",
        subheader: 'by {authorName}',
      },
    },
  },
  variant_b3: {
    configuredText: {
      buttonsPage: {
        header: "Free academic library — including '{paperTitle}'",
        subheader: 'Sign up for instant access',
      },
    },
  },
  variant_b4: {
    forkComponent: B4PaperThumbnailAbove,
    forkLoader: b4PaperThumbnailAboveLoader,
  },
  variant_b5: {
    configuredText: {
      emailRegistrationPage: {
        subheader: "You're 30 seconds from reading '{paperTitle}'",
      },
    },
  },
  variant_b6: {
    configuredText: {
      buttonsPage: {
        header: "To read '{paperTitle}' please sign up",
      },
      emailEntryPage: {
        subheader: "To read '{paperTitle}'",
      },
      emailRegistrationPage: {
        subheader: "You're 30 seconds from reading '{paperTitle}'",
      },
    },
  },
  variant_c1: {
    configuredText: {
      buttonsPage: {
        header: 'Step 1 of 1: sign up',
      },
    },
  },
  variant_c2: {
    configuredText: {
      buttonsPage: {
        subheader: '5 seconds with Google — really',
      },
    },
  },
  variant_c3: {
    configuredText: {
      emailRegistrationPage: {
        subheader: 'Last step — almost there',
      },
    },
  },
  variant_d1: {
    configuredText: {
      buttonsPage: {
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        emailButton: 'Download with Email',
        disclaimerVerb: 'Download',
      },
    },
  },
  variant_d2: {
    configuredText: {
      buttonsPage: {
        googleButton: 'Sign up & continue with Google',
        appleButton: 'Sign up & continue with Apple',
        emailButton: 'Sign up & continue with Email',
        disclaimerVerb: 'Sign up & continue',
      },
    },
  },
  variant_d3: {
    configuredText: {
      emailRegistrationPage: {
        submit: 'Download',
      },
    },
  },
  variant_f1: {
    forkComponent: F1EmailButtonOnTop,
    forkLoader: f1EmailButtonOnTopLoader,
  },
  variant_f2: { forkComponent: F2NoFacebook, forkLoader: f2NoFacebookLoader },
  variant_f4: { forkComponent: F4GoogleOnly, forkLoader: f4GoogleOnlyLoader },
  variant_f5: {
    forkComponent: F5GoogleOnlyWithPaperLine,
    forkLoader: f5GoogleOnlyWithPaperLineLoader,
    configuredText: {
      buttonsPage: { paperContextText: "To read '{paperTitle}'" },
    },
  },
  variant_h1: {
    forkComponent: H1ComboPaperSpeedGoogleDownload,
    forkLoader: h1ComboPaperSpeedGoogleDownloadLoader,
    configuredText: {
      buttonsPage: {
        header: "To read '{paperTitle}' please sign up",
        subheader: '5 seconds with Google — really',
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        facebookButton: 'Download with Facebook',
        emailButton: 'Download with Email',
        disclaimerVerb: 'Download',
      },
    },
  },
  variant_h2: {
    configuredText: {
      buttonsPage: {
        header: "To read '{paperTitle}' please sign up",
        subheader: '5 seconds with Google — really',
      },
    },
  },
  variant_h3: {
    forkComponent: H3GoogleOnlyWithSpeed,
    forkLoader: h3GoogleOnlyWithSpeedLoader,
    configuredText: {
      buttonsPage: {
        subheader: '5 seconds with Google — really',
      },
    },
  },
  variant_h4: {
    forkComponent: H4YourPaperCardGoogleOnly,
    forkLoader: h4YourPaperCardGoogleOnlyLoader,
    configuredText: {
      buttonsPage: {
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        emailButton: 'Download with Email',
        disclaimerVerb: 'Download',
      },
    },
  },
  variant_h5: {
    forkComponent: H5YourPaperCardThreeMethods,
    forkLoader: h5YourPaperCardThreeMethodsLoader,
    configuredText: {
      buttonsPage: {
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        emailButton: 'Download with Email',
        disclaimerVerb: 'Download',
      },
    },
  },
};

export function lookupModalVariant(bucket: string): ModalVariantEntry {
  return MODAL_VARIANT_REGISTRY[bucket] ?? {};
}

// Deep-merges a per-page PartialConfigurableText override on top of a
// caller-supplied PartialConfigurableText. The variant's keys win where
// they overlap; the caller's keys remain for any page or field the
// variant doesn't touch. Phase 1 variants all have empty overrides so
// this is a no-op — but Phase 2 variants depend on it.
export function mergeConfiguredText(
  caller: PartialConfigurableText | undefined,
  variant: PartialConfigurableText | undefined,
): PartialConfigurableText | undefined {
  if (!variant) return caller;
  if (!caller) return variant;
  return {
    buttonsPage: { ...caller.buttonsPage, ...variant.buttonsPage },
    emailEntryPage: { ...caller.emailEntryPage, ...variant.emailEntryPage },
    emailRegistrationPage: {
      ...caller.emailRegistrationPage,
      ...variant.emailRegistrationPage,
    },
    emailLoginPage: { ...caller.emailLoginPage, ...variant.emailLoginPage },
  };
}

// Work-context substitution for autotest variant copy.
//
// Variants that want to acknowledge the specific paper a visitor is
// trying to access (Cluster B — paper anchoring) can put a `{paperTitle}`
// or `{authorName}` token in any configuredText string. At modal-open
// time, tokens are replaced with `workTitle` / `authorName` from the
// caller's event detail. Callers without a work context (non-LOSWP
// callers, or LOSWP callers that don't pass workTitle / authorName)
// fall back to a generic phrase so the copy still reads correctly.
//
// The author-name caller decides display formatting (e.g. "Jane Doe"
// vs "Jane Doe and others") so this layer doesn't need to know about
// author count.
//
// Token format: `{paperTitle}` / `{authorName}`. Single brace-delimited
// identifier; unlikely to collide with intentional literal copy.
export const PAPER_TITLE_FALLBACK = 'this paper';
export const AUTHOR_NAME_FALLBACK = 'the authors';

export type WorkContext = {
  workTitle?: string;
  authorName?: string;
};

export function substituteWorkContext(
  text: PartialConfigurableText | undefined,
  ctx: WorkContext,
): PartialConfigurableText | undefined {
  if (!text) return text;
  const title = ctx.workTitle?.trim() || PAPER_TITLE_FALLBACK;
  const author = ctx.authorName?.trim() || AUTHOR_NAME_FALLBACK;
  // Function replacer (not string replacer) so values containing `$&`, `$1`,
  // etc. don't get interpreted as String.replace special tokens.
  const sub = (s: string): string =>
    s
      .replace(/\{paperTitle\}/g, () => title)
      .replace(/\{authorName\}/g, () => author);

  // Per-field substitution that preserves the input's own-key set exactly:
  // absent keys stay absent in the output. The previous implementation listed
  // every field as `field: sub(text.field)`, materialising `field: undefined`
  // for fields the source didn't set; those explicit-undefined values then
  // clobbered the defaults in LoginModal's spread merge and blanked out
  // auth-button labels for every bucket (including control).
  const subFields = <T extends Record<string, string | undefined>>(
    obj: T,
  ): T => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined) out[k] = sub(v);
    }
    return out as T;
  };

  return {
    buttonsPage: text.buttonsPage && subFields(text.buttonsPage),
    emailEntryPage: text.emailEntryPage && subFields(text.emailEntryPage),
    emailRegistrationPage:
      text.emailRegistrationPage && subFields(text.emailRegistrationPage),
    emailLoginPage: text.emailLoginPage && subFields(text.emailLoginPage),
  };
}
